import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  Dialog,
  IconButton,
  TablePagination,
  Tooltip,
} from "@mui/material";
import type { ChangeEvent, MouseEvent } from "react";
import {
  Close as CloseIcon,
  DeleteForeverOutlined as DeleteForeverOutlinedIcon,
  EditLocationAlt as EditLocationAltIcon,
} from "@mui/icons-material";
import PopoverRegister from "../../components/PopoverRegister";
import PopoverDelete from "../../components/PopoverDelete";
import { getTenants, updateTenant, deleteTenant } from "../../api";
import TextFieldValidate from "./TextFieldValidate";
import DatepickerValidate from "./DatepickerValidate";
import SnackbarCustom, {
  type SnackbarData,
} from "../../components/SnackBarCustom";
import type {
  TenantEditableColumn,
  TenantItem,
  TenantTableColumn,
} from "../../types/tenant";

const columns: TenantTableColumn[] = [
  {
    id: "name",
    label: "会社名",
    minWidth: "80px",
    textfield: true,
    isSmallScreen: true,
  },
  {
    id: "code",
    label: "識別子",
    minWidth: "80px",
    textfield: true,
    isSmallScreen: true,
  },
  {
    id: "manager_name",
    label: "契約担当者",
    minWidth: "80px",
    textfield: true,
    isSmallScreen: false,
  },
  {
    id: "manager_tel",
    label: "担当者連絡先",
    minWidth: "120px",
    textfield: true,
    isSmallScreen: false,
  },
  {
    id: "contract_start_at",
    label: "契約開始年月日",
    minWidth: "80px",
    datepicker: true,
    isSmallScreen: false,
  },
  {
    id: "contract_end_at",
    label: "契約終了年月日",
    minWidth: "80px",
    datepicker: true,
    isSmallScreen: false,
  },
  {
    id: "buttonSpace",
    label: "",
    minWidth: "176px",
    button: true,
    isSmallScreen: true,
  },
];

const formatTenantDate = (value: string | Date | undefined): string => {
  const date = new Date(value ?? "");
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}/${month}/${day}`;
};

export default function SubscriberTable() {
  const navigate = useNavigate();
  const handleClick = (path: string) => {
    navigate(path);
  };

  const [selectedRowNum, isSelectedRowNum] = useState<number | "">("");
  const [registerPopoverOpen, setRegisterPopoverOpen] = useState(false);
  const registerButtonClick = (rowNum: number) => {
    isSelectedRowNum(rowNum);
    setRegisterPopoverOpen(true);
  };
  const registerPopupClose = () => {
    setRegisterPopoverOpen(false);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [totalData, setTotalData] = useState<number | undefined>();

  const getTenantsData = async () => {
    try {
      const response = await getTenants({
        code: "",
        name: "",
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status === "ok") {
        const data = response.m_tenants;
        const total = response.total_count;
        console.log("テナント一覧取得", response);
        console.log("total_dataデータ型", typeof total);
        setTenants(data);
        setTotalData(total);
        setSnackBarstatus(response);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    getTenantsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const postTenantData = async (isSuccess: boolean) => {
    if (!isSuccess || selectedRowNum === "") {
      console.log("何も行いません");
      return;
    }

    const selectedTenant = tenants[selectedRowNum];
    if (!selectedTenant) {
      return;
    }

    const formattedDateStr = formatTenantDate(selectedTenant.contract_start_at);
    const formattedDateEnd = formatTenantDate(selectedTenant.contract_end_at);
    console.log(selectedTenant);

    const updatedTenantData = {
      id: selectedTenant.id,
      code: selectedTenant.code,
      name: selectedTenant.name,
      manager_name: selectedTenant.manager_name,
      manager_tel: selectedTenant.manager_tel,
      contract_start_at: formattedDateStr,
      contract_end_at: formattedDateEnd,
    };
    console.log("updatedTenantData", updatedTenantData);
    try {
      const status = await updateTenant(updatedTenantData, {
        id: selectedTenant.id,
      });
      console.log("レスポンスデータ:", status);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
  const deleteButtonClick = (tenantId: number) => {
    setDeleteTenantId(tenantId);
    setDeletePopoverOpen(true);
  };
  const deletePopupClose = () => {
    setDeletePopoverOpen(false);
  };
  const [deleteTenantId, setDeleteTenantId] = useState<number | "">("");

  const handleDelete = async (isSuccess: boolean) => {
    if (!isSuccess || deleteTenantId === "") {
      console.log("何も行いません");
      return;
    }

    try {
      const status = await deleteTenant(deleteTenantId);
      if (status.status === "ok") {
        console.log(`テナント ${deleteTenantId} を削除しました`);
        setSnackBarstatus(status);
        setSnackbarOpen(true);
        getTenantsData();
      } else if (status.status === "ng") {
        console.error("指定したテナントは存在しません");
        setSnackBarstatus(status);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("リクエストエラー:", error);
    }
  };

  const handleChange = (
    inputValue: string | null,
    _tenantId: number,
    rowNum: number,
    column: TenantEditableColumn
  ) => {
    console.log("column", typeof column);
    const updatedArray = [...tenants];
    const updatedItem = { ...updatedArray[rowNum] };
    updatedItem[column] = (inputValue ?? "") as TenantItem[TenantEditableColumn];
    updatedArray[rowNum] = updatedItem;
    setTenants(updatedArray);
    console.log(inputValue, _tenantId, rowNum);
    console.log("tenants", tenants);
  };

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (rowNum: number) => {
    isSelectedRowNum(rowNum);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const buttonVaridate = (
    input: boolean | undefined,
    tenant: TenantItem,
    rowNum: number
  ) =>
    input ? (
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          justifyContent: "flex-end",
          ".edit-button": {
            display: "none",
          },
          "@media screen and (max-width:900px)": {
            ".submit-button": {
              display: "none",
            },
            ".edit-button": {
              display: "unset",
            },
          },
        }}
      >
        <Button
          variant="contained"
          className="submit-button"
          sx={{ pointerEvents: "auto" }}
          onClick={() => registerButtonClick(rowNum)}
        >
          登録
        </Button>
        <Button
          variant="contained"
          className="edit-button"
          sx={{ pointerEvents: "auto" }}
          onClick={() => handleLocationCodeClick(rowNum)}
        >
          編集
        </Button>
        <Tooltip title="アイコン編集">
          <IconButton
            color="primary"
            sx={{ pointerEvents: "auto" }}
            onClick={() =>
              handleClick(
                `/tenant/setting?tenant_id=${tenant.id}&name=${tenant.name}`
              )
            }
          >
            <EditLocationAltIcon />
          </IconButton>
        </Tooltip>
        <IconButton
          color="error"
          sx={{ pointerEvents: "auto" }}
          onClick={() => deleteButtonClick(tenant.id)}
        >
          <DeleteForeverOutlinedIcon />
        </IconButton>
      </Box>
    ) : null;

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const selectedTenant =
    selectedRowNum === "" ? undefined : tenants[selectedRowNum];

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    color: "outline.dark",
                    fontWeight: "bold",
                    borderBottom: 2,
                    padding: "8px",
                    "@media screen and (max-width:900px)": {
                      display:
                        column.isSmallScreen === true ? "table-cell" : "none",
                    },
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((tenant, rowNum) => {
              return (
                <TableRow
                  role="checkbox"
                  tabIndex={-1}
                  key={tenant.id}
                  sx={{
                    marginTop: "8px",
                    pointerEvents: "none",
                  }}
                >
                  {columns.map((column) => {
                    return (
                      <TableCell
                        key={column.id}
                        sx={{
                          padding: "8px",
                          "@media screen and (max-width:900px)": {
                            display:
                              column.isSmallScreen === true
                                ? "table-cell"
                                : "none",
                          },
                        }}
                      >
                        {column.textfield === true ? (
                          <TextFieldValidate
                            input={tenant}
                            column={column.id as TenantEditableColumn}
                            changedValue={(value) =>
                              handleChange(
                                value,
                                tenant.id,
                                rowNum,
                                column.id as TenantEditableColumn
                              )
                            }
                          />
                        ) : column.datepicker === true ? (
                          <DatepickerValidate
                            input={tenant}
                            column={column.id as TenantEditableColumn}
                            changedValue={(value) =>
                              handleChange(
                                value,
                                tenant.id,
                                rowNum,
                                column.id as TenantEditableColumn
                              )
                            }
                          />
                        ) : null}
                        {buttonVaridate(column.button, tenant, rowNum)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={isPopoverOpen}
        onClose={handlePopoverClose}
        sx={{
          ".css-yiavyu-MuiBackdrop-root-MuiDialog-backdrop": {
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
          }}
        >
          <Box
            sx={{
              paddingLeft: "8px",
              fontWeight: "bold",
              fontSize: "20px",
            }}
          >
            契約者マスタ詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={handlePopoverClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {columns.map((column) => {
              return (
                <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                  <TableCell
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      boxSizing: "border-box",
                      width: "calc((100vw - 64px) / 2)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        WebkitWritingMode: "horizontal-tb",
                        height: "100%",
                        padding: "8px",
                      }}
                    >
                      {column.label}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      writingMode: "horizontal-tb",
                      boxSizing: "border-box",
                      width: "calc((100% - 64px) / 2)",
                      overflowWrap: "break-word",
                      ".edit-button": {
                        display: "none",
                      },
                      ".submit-button": {
                        display: "unset",
                      },
                    }}
                  >
                    {selectedTenant && column.textfield === true ? (
                      <TextFieldValidate
                        input={selectedTenant}
                        column={column.id as TenantEditableColumn}
                        changedValue={(value) =>
                          handleChange(
                            value,
                            selectedTenant.id,
                            selectedRowNum as number,
                            column.id as TenantEditableColumn
                          )
                        }
                      />
                    ) : selectedTenant && column.datepicker === true ? (
                      <DatepickerValidate
                        input={selectedTenant}
                        column={column.id as TenantEditableColumn}
                        changedValue={(value) =>
                          handleChange(
                            value,
                            selectedTenant.id,
                            selectedRowNum as number,
                            column.id as TenantEditableColumn
                          )
                        }
                      />
                    ) : null}
                    {selectedTenant &&
                      buttonVaridate(
                        column.button,
                        selectedTenant,
                        selectedRowNum as number
                      )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Dialog>
      <PopoverDelete
        open={deletePopoverOpen}
        onClose={deletePopupClose}
        yesNo={handleDelete}
      />
      <PopoverRegister
        open={registerPopoverOpen}
        onClose={registerPopupClose}
        yesNo={(isSuccess) => postTenantData(isSuccess)}
      />
      <TablePagination
        rowsPerPageOptions={[10, 30, 50, 100]}
        component="div"
        count={totalData ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions": {
            margin: "0 !important",
            padding: "0 !important",
          },
        }}
      />
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
    </Paper>
  );
}
